// תוכנית קנייה לרשימת הקניות (BasketTab → ShoppingPlan.jsx): איפה הכי משתלם לקנות את כל הרשימה.
//
// בודקים כל צירוף של חנויות (כל חנות לבד, כל זוג, כל שלישייה...). בכל צירוף - כל מוצר נקנה בחנות הזולה ביותר מתוכו.
// נשארות רק תוכניות שמכסות את כל המוצרים (מוצר שלא נמכר באף חנות לא נכלל בחישוב - מסומן ברשימה עצמה).
// לכל מספר חנויות (1, 2, 3...) - התוכנית הזולה ביותר. ההמלצה מתחילה מהכי מעט חנויות, ועוברת לתוכנית עם יותר חנויות
// רק אם היא חוסכת לפחות threshold ש"ח לכל חנות נוספת - חיסכון של ₪2 לא שווה נסיעה לסופר נוסף.
// עד 6 חנויות = 63 צירופים × 100 מוצרים - חישוב מיידי בדפדפן, בלי שרת.

const EPS = 0.005;
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// משלוח מחנות לסכום מוצרים מסוים (stores.deliveryMinOrder / deliveryFee / deliveryFreeFrom, בק אופיס → חנויות):
// { possible, fee, feeUnknown, toFree } - toFree = כמה חסר למשלוח חינם
export const deliveryFor = (store, subtotal) => {
  const min = num(store.deliveryMinOrder);
  const fee = num(store.deliveryFee);
  const freeFrom = num(store.deliveryFreeFrom);
  if (min !== null && subtotal < min - EPS) return { possible: false, min };
  if (freeFrom !== null && subtotal >= freeFrom - EPS) return { possible: true, fee: 0 };
  return { possible: true, fee: fee ?? 0, feeUnknown: fee === null, toFree: freeFrom !== null ? freeFrom - subtotal : null };
};

// עלות שורה (מוצר × כמות) בחנות, כולל מגבלת כמות במבצע (stores.saleQtyLimit, בק אופיס → חנויות):
// מחיר המבצע חל רק על saleQtyLimit יחידות, וכל יחידה נוספת במחיר הרגיל (למשל בשוליק - "בד"כ 3 יח'").
// מבצע = המחיר להשוואה (p.price) נמוך מהמחיר הרגיל (p.regularPrice), ובלי עדכון משתמש (עדכון משתמש = מחיר רגיל, בלי מגבלה).
// לא חל על מוצר שקיל (הכמות בק"ג). משמש בתוכנית הקנייה, בסכומי החנויות ובטבלת הפירוט (BasketTab) - חישוב אחד, מקום אחד.
// מחזיר { cost, unitPrice, limited } - limited = חלק מהיחידות חושבו במחיר הרגיל
export const lineCost = (p, store, item) => {
  const price = Number(p.price);
  const regular = Number(p.regularPrice);
  const limit = num(store.saleQtyLimit);
  const onSale = p.reportedPrice == null && regular > price + EPS;
  if (!onSale || limit === null || item.isWeighable || item.qty <= limit) {
    return { cost: price * item.qty, unitPrice: price, limited: false };
  }
  return { cost: price * limit + regular * (item.qty - limit), unitPrice: price, limited: true, limit, regularPrice: regular };
};

// list: [{ id, name, qty, isWeighable }]; stores: [{ id, name, delivery*, saleQtyLimit }];
// pricesByItem: { [itemId]: { [storeId]: { price, regularPrice, reportedPrice } } }
// delivery = true: לכל חנות בתוכנית נוספים דמי המשלוח שלה, ותוכנית שבה חנות מתחת למינימום ההזמנה שלה - לא אפשרית.
//   (בכל צירוף המוצרים מחולקים לפי המחיר בלבד; צירוף שבו חנות לא מגיעה למינימום נפסל, וצירוף קטן יותר מכסה את המקרה.)
export const computePlans = (list, stores, pricesByItem, { delivery = false } = {}) => {
  const priced = list.filter((x) => pricesByItem[x.id] && Object.keys(pricesByItem[x.id]).length > 0);
  if (!priced.length || !stores.length) return { priced, levels: [] };

  // בשוויון מחיר - החנות שיש בה יותר מהרשימה, כדי לא לפצל בלי סיבה
  const coverage = (st) => priced.filter((x) => pricesByItem[x.id][st.id]).length;
  const ordered = [...stores].sort((a, b) => coverage(b) - coverage(a) || a.id - b.id);

  const bestBySet = {};
  for (let mask = 1; mask < (1 << ordered.length); mask++) {
    const allowed = ordered.filter((_, i) => mask & (1 << i));
    const groups = new Map();
    let total = 0;
    let complete = true;

    for (const x of priced) {
      // הזולה לפי עלות השורה כולה (לא מחיר ליחידה) - מגבלת כמות במבצע יכולה לשנות מי הזולה
      let pick = null;
      for (const st of allowed) {
        const p = pricesByItem[x.id][st.id];
        if (!p) continue;
        const line = lineCost(p, st, x);
        if (!pick || line.cost < pick.line.cost - EPS) pick = { store: st, line };
      }
      if (!pick) { complete = false; break; }
      const { cost } = pick.line;
      total += cost;
      if (!groups.has(pick.store.id)) groups.set(pick.store.id, { store: pick.store, items: [], total: 0 });
      const g = groups.get(pick.store.id);
      g.items.push({ item: x, price: pick.line.unitPrice, cost, limited: pick.line.limited, limit: pick.line.limit });
      g.total += cost;
    }
    if (!complete) continue;

    // צירוף שבו חנות לא קיבלה אף מוצר = בעצם צירוף קטן יותר
    const used = [...groups.values()].sort((a, b) => b.total - a.total);

    // משלוח: דמי המשלוח של כל חנות; חנות מתחת למינימום → הצירוף לא אפשרי
    if (delivery) {
      let possible = true;
      used.forEach((g) => {
        g.itemsTotal = g.total;
        g.delivery = deliveryFor(g.store, g.total);
        if (!g.delivery.possible) possible = false;
        else { g.total += g.delivery.fee; total += g.delivery.fee; }
      });
      if (!possible) continue;
    }
    const key = used.map((g) => g.store.id).sort((a, b) => a - b).join(',');
    if (!bestBySet[key] || total < bestBySet[key].total - EPS) bestBySet[key] = { key, groups: used, total, storeCount: used.length };
  }

  // הזולה ביותר לכל מספר חנויות
  const byCount = {};
  Object.values(bestBySet).forEach((p) => {
    if (!byCount[p.storeCount] || p.total < byCount[p.storeCount].total - EPS) byCount[p.storeCount] = p;
  });
  const levels = Object.values(byCount).sort((a, b) => a.storeCount - b.storeCount);

  // תוכנית עם יותר חנויות שלא חוסכת כלום - לא מעניינת
  const useful = levels.filter((p, i) => i === 0 || p.total < levels[i - 1].total - EPS);
  return { priced, levels: useful };
};

const formatPrice = (n) => `₪${Number(n || 0).toFixed(2)}`;
const qtyText = (x) => (x.isWeighable ? ` - ${x.qty} ק"ג` : x.qty !== 1 ? ` ×${x.qty}` : '');
const today = () => new Date().toLocaleDateString('he-IL');

// שורת המשלוח של חנות בתוכנית (ShoppingPlan, וואטסאפ, הדפסה)
export const deliveryText = (d) => (d.fee === 0 && !d.feeUnknown ? 'משלוח חינם'
  : d.feeUnknown ? 'דמי משלוח: לא ידוע'
  : `משלוח: ${formatPrice(d.fee)}`);

// מוצרים ברשימה שלא נכנסו לתוכנית (לא נמכרים כרגע באף חנות)
export const itemsNotInPlan = (plan, items) => {
  const inPlan = new Set(plan.groups.flatMap((g) => g.items.map(({ item }) => item.id)));
  return items.filter((x) => !inPlan.has(x.id));
};

export const planTitle = (plan) => (plan.storeCount === 1
  ? `הכי משתלם: הכל ב${plan.groups[0].store.name}`
  : `הכי משתלם: לפצל בין ${plan.groups.length} חנויות`);

// התוכנית כטקסט לוואטסאפ (BasketTab, ShoppingPlan): המוצרים מקובצים לפי חנות, עם מחיר לכל שורה וסכום
export const planToText = (plan, items) => {
  const lines = [`🛒 רשימת קניות - זול בכפר (${today()})`, ''];
  plan.groups.forEach((g) => {
    lines.push(`*${g.store.name}* - ${formatPrice(g.total)}`);
    g.items.forEach(({ item, cost, limited, limit }) => lines.push(
      `• ${item.name}${qtyText(item)} - ${formatPrice(cost)}${limited ? ` (מבצע עד ${limit} יח')` : ''}`));
    if (g.delivery) lines.push(`🚚 ${deliveryText(g.delivery)}`);
    lines.push('');
  });
  const rest = itemsNotInPlan(plan, items);
  if (rest.length) {
    lines.push('*לא נמכר כרגע באף חנות:*');
    rest.forEach((x) => lines.push(`• ${x.name}${qtyText(x)}`));
    lines.push('');
  }
  lines.push(`סה"כ ${formatPrice(plan.total)}${plan.storeCount > 1 ? ` (${plan.storeCount} חנויות)` : ''}`);
  lines.push(window.location.origin);
  return lines.join('\n');
};

// ההמלצה: מהכי מעט חנויות, ועוברים לתוכנית עם יותר חנויות רק אם היא חוסכת לפחות threshold לכל חנות נוספת
export const recommendPlan = (levels, threshold) => {
  if (!levels.length) return null;
  let rec = levels[0];
  for (const p of levels.slice(1)) {
    const extraStores = p.storeCount - rec.storeCount;
    if (rec.total - p.total >= threshold * extraStores - EPS) rec = p;
  }
  return rec;
};
